import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { email, form, FormField, required } from '@angular/forms/signals';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  CuiButtonComponent,
  CuiDatepickerComponent,
  CuiInputComponent,
  CuiPasswordComponent,
  CuiSelectComponent,
  CuiTabsComponent,
  SelectOption,
  TabItem,
} from '@idealink-material/ui-core';
import { CuiIconComponent } from '@idealink-material/ui-icons';
import { ProfileDialogService } from '@/share/services/profile-dialog.service';
import { ProfileHttpService } from '@/core/service/profile-http.service';
import { SystemDateFormatService } from '@/core/service/system-date-format.service';
import { ChangePasswordModel, ProfileModel } from '@/core/model/profile.model';

@Component({
  selector: 'app-profile-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    FormField,
    TranslatePipe,
    CuiButtonComponent,
    CuiInputComponent,
    CuiPasswordComponent,
    CuiSelectComponent,
    CuiDatepickerComponent,
    CuiTabsComponent,
    CuiIconComponent,
  ],
  templateUrl: './profile-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileDialog {
  private readonly svc = inject(ProfileDialogService);
  private readonly profileHttp = inject(ProfileHttpService);
  private readonly dateFormatService = inject(SystemDateFormatService);
  private readonly translate = inject(TranslateService);

  readonly isOpen = this.svc.isOpen;
  readonly dateFormat = computed(() => this.dateFormatService.mapToPrimeNgDateFormat());

  // p-tabs/p-select render `label` as a plain string (no translate pipe inside the library),
  // so labels are resolved up front rather than passed as translation keys.
  readonly tabs: TabItem[] = [
    { id: 'account', label: this.translate.instant('profile.tabs.account') },
    { id: 'security', label: this.translate.instant('profile.tabs.security') },
  ];
  readonly activeTab = signal(0);

  readonly genderOptions: SelectOption<string>[] = [
    { value: 'male', label: this.translate.instant('profile.genderOption.male') },
    { value: 'female', label: this.translate.instant('profile.genderOption.female') },
  ];

  // Seeded with local mock data — this dialog isn't wired to AuthService's session user yet.
  readonly accountModel = signal<ProfileModel>({
    fullName: 'Moni Roy',
    nameKhmer: 'ម៉ូនី រយ',
    username: 'KIKI',
    dateOfBirth: new Date(2004, 1, 13),
    email: 'moniroy@gmail.com',
    gender: 'female',
    avatarUrl: 'https://i.pravatar.cc/120?img=47',
  });
  readonly accountForm = form(this.accountModel, (f) => {
    required(f.fullName, { message: 'profile.required.fullName' });
    required(f.username, { message: 'profile.required.username' });
    required(f.email, { message: 'profile.required.email' });
    email(f.email, { message: 'profile.invalidEmail' });
  });

  readonly passwordModel = signal<ChangePasswordModel>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  readonly passwordForm = form(this.passwordModel, (f) => {
    required(f.currentPassword, { message: 'profile.required.currentPassword' });
    required(f.newPassword, { message: 'profile.required.newPassword' });
    required(f.confirmPassword, { message: 'profile.required.confirmPassword' });
  });

  readonly isSavingAccount = signal(false);
  readonly isSavingPassword = signal(false);

  selectTab(index: number): void {
    this.activeTab.set(index);
  }

  close(): void {
    this.svc.close();
  }

  saveAccount(): void {
    if (this.accountForm().invalid()) return;
    this.isSavingAccount.set(true);
    this.profileHttp.updateProfile(this.accountForm().value()).subscribe({
      next: () => this.isSavingAccount.set(false),
      error: () => this.isSavingAccount.set(false),
    });
  }

  savePassword(): void {
    if (this.passwordForm().invalid()) return;
    this.isSavingPassword.set(true);
    this.profileHttp.changePassword(this.passwordForm().value()).subscribe({
      next: () => {
        this.isSavingPassword.set(false);
        this.passwordModel.set({ currentPassword: '', newPassword: '', confirmPassword: '' });
      },
      error: () => this.isSavingPassword.set(false),
    });
  }
}
